import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import styles from './SupportContent.module.css';
import backIcon from '../icons/back_icn.png';
import { FiPaperclip, FiSend, FiX, FiChevronDown } from 'react-icons/fi';

const SupportContent = ({ onBack, isAuthenticated, setIsAuthenticated }) => {
  const contentRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      axios
        .get('/api/users/verify-token', {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data.valid) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            localStorage.removeItem('authToken');
            localStorage.removeItem('userId');
          }
        })
        .catch(() => {
          setIsAuthenticated(false);
          localStorage.removeItem('authToken');
          localStorage.removeItem('userId');
        });
    } else {
      setIsAuthenticated(false);
    }
  }, [setIsAuthenticated]);

  const fetchSupportThread = useCallback(() => {
    if (!isAuthenticated) {
      setMessages([]);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      setMessages([]);
      return;
    }
    axios
    .get('/api/support/thread', {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setMessages(res.data);
      } else {
        setMessages([]);
      }
    })
    .catch((err) => {
      setMessages([]);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSupportThread();
    window.addEventListener('supportThreadChanged', fetchSupportThread);
    return () => {
      window.removeEventListener('supportThreadChanged', fetchSupportThread);
    };
  }, [fetchSupportThread]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    const eventSource = new EventSource(`/api/support/sse?token=${token}`);

    eventSource.onmessage = (event) => {
      try {
        const newMsg = JSON.parse(event.data);
        setMessages((prev) => [...prev, newMsg]);
      } catch (err) {
      }
    };

    return () => {
      eventSource.close();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const handleScroll = () => {
      const nearBottom =
        content.scrollTop + content.clientHeight >= content.scrollHeight - 50;
      setIsAtBottom(nearBottom);
    };

    content.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      content.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    if (isAtBottom && messages.length > 0) {
      const content = contentRef.current;
      content.scrollTo({ top: content.scrollHeight });
    }
  }, [messages, isAtBottom]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    setAttachments(files);
  };

  const handleSubmit = (e) => {
  e.preventDefault();
  if (!message.trim()) return;

  const token = localStorage.getItem('authToken');
  if (!token) return;

  const formData = new FormData();
  formData.append('text', message);
  attachments.forEach((file) => formData.append('attachments', file));

  setShowSpinner(true);

  axios
    .post('/api/support/send', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(() => {
      setMessage('');
      setAttachments([]);
      fetchSupportThread();
    })
    .catch((err) => {
    })
    .finally(() => {
      setShowSpinner(false);
    })
};

  const metalColor = '#c0c0c0';

  return (
    <div className={styles.supportContainer}>
      <img
        src={backIcon}
        alt="Back"
        className={`${styles.backIcon} ${styles.visible}`}
        onClick={onBack}
      />
      <h1 className={styles.pageTitle}>Повідомлення</h1>

      <div className={styles.supportContent} ref={contentRef}>
        {isAuthenticated ? (
          <>
            {messages.length > 0 ? (
              messages.map((msg) => {
                const isUser = msg.from === 'user';
                return (
                  <div
                    key={msg.id}
                    className={isUser ? styles.ownMessage : styles.theirMessage}
                  >
                    <div className={styles.messageText}>
		     {msg.text.split('\n').map((line, i) => (
		       <React.Fragment key={i}>
		         {line || <>&nbsp;</>} 
		         <br />     
		       </React.Fragment>
		     ))}
	           </div>
		    {msg.attachments?.length > 0 && (
                    <ul className={styles.attachmentList}>
                      {msg.attachments.map((att, i) => (
                        <li key={i}>
                          <button
                            className={styles.downloadButton}
                            onClick={async () => {
                              const token = localStorage.getItem('authToken');
                              if (!token) return;

                              try {
                                setDownloadProgress(0);

                                await axios.head(
                                  `/api/support/attachment/${encodeURIComponent(att.path)}`,
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  }
                                );

                                const response = await axios.get(
                                  `/api/support/attachment/${encodeURIComponent(att.path)}`,
                                  {
                                    responseType: 'blob',
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                    onDownloadProgress: (event) => {
                                      const percent = Math.round((event.loaded * 100) / event.total);
                                      setDownloadProgress(percent);
                                    },
                                  }
                                );

                                const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.setAttribute('download', att.originalName);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(blobUrl);
                              } catch (err) {
                                alert('Ой, вибач! Спробуй пізніше.');
                              } finally {
                                setDownloadProgress(null);
                              }
                            }}
                          >
                            📎 {att.originalName}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                    <span className={styles.messageDate}>
                      {new Date(msg.createdAt).toLocaleString()}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className={styles.noMessages}>Ще немає повідомлень.</p>
            )}
	    {messages.length > 0 && !isAtBottom && (
	  <button
	    className={styles.scrollToBottomButton}
	    onClick={() => {
	      const content = contentRef.current;
	      if (content) {
		content.scrollTo({ top: content.scrollHeight, behavior: 'smooth' });
	      }
	    }}
	  >
	    <FiChevronDown size={24} />
	  </button>
	)}
           </>
      ) : (
        <p className={styles.notAuthenticated}>
         Потрібно увійти, щоб побачити повідомлення.
        </p>
      )}
    </div>

    {isAuthenticated && (
      <form onSubmit={handleSubmit} className={styles.supportFormFixed}>
        <textarea
          className={styles.messageField}
          placeholder="Писати тут"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

       {attachments.length > 0 && (
      <ul className={styles.attachmentPreview}>
        {attachments.map((file, index) => (
          <li key={index}>
            {file.name}
            <button
              type="button"
              onClick={() => {
                const updated = [...attachments];
                updated.splice(index, 1);
                setAttachments(updated);
              }}
            >
              <FiX color={metalColor} size={18} />
            </button>
          </li>
        ))}
      </ul>
    )}
       
       <div className={styles.actionRow}>
	    <label className={styles.fileInputLabel}>
	      <FiPaperclip color={metalColor} size={18} style={{ marginRight: 6 }} />
	      Файли
	      <input
		type="file"
		multiple
		accept=".jpg,.jpeg,.png,.pdf"
		onChange={handleFileChange}
		className={styles.hiddenFileInput}
	      />
	    </label>

	    <button type="submit" className={styles.sendButton}>
	      <FiSend color={metalColor} size={18} style={{ marginRight: 6 }} />
	      Відправити
	    </button>
	  </div>
	</form>
    )}

      {downloadProgress !== null && (
      <div className={styles.progressOverlay}>
        <div className={styles.progressText}>{downloadProgress}%</div>
      </div>
    )}
     {showSpinner && (
	  <div className={styles.spinnerOverlay}>
	    <div className={styles.spinnerLoader}></div>
	  </div>
	)}
    </div>
  );
};

export default SupportContent;

